import React, { useState } from 'react';
import { Mail, Linkedin, Instagram, ArrowRight, CheckCircle, AlertCircle, User, Building2 } from 'lucide-react';

const Contact: React.FC = () => {
  const [userType, setUserType] = useState<'individual' | 'institution'>('individual');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    // Individual specific
    currentStatus: '',
    skillToMonetize: '',
    idealClient: '',
    biggestChallenge: '',
    // Institution specific
    organizationName: '',
    website: '',
    partnershipType: '',
    message: ''
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Helper to encode data for Netlify
  const encode = (data: any) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Only include fields relevant to the current user type
    const relevantFields = userType === 'individual' 
      ? {
          name: formData.name,
          email: formData.email,
          currentStatus: formData.currentStatus,
          skillToMonetize: formData.skillToMonetize,
          idealClient: formData.idealClient,
          biggestChallenge: formData.biggestChallenge
        }
      : {
          name: formData.name,
          email: formData.email,
          organizationName: formData.organizationName,
          website: formData.website,
          partnershipType: formData.partnershipType,
          message: formData.message
        };

    const submissionData = {
        "form-name": "contact",
        "user-type": userType,
        ...relevantFields
    };

    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode(submissionData)
    })
      .then(() => {
        setIsSubmitted(true);
        // Reset form data after successful submission
        setFormData({
          name: '',
          email: '',
          currentStatus: '',
          skillToMonetize: '',
          idealClient: '',
          biggestChallenge: '',
          organizationName: '',
          website: '',
          partnershipType: '',
          message: ''
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((error) => {
        console.error(error);
        setError("Something went wrong. Please try again later or email us directly.");
      });
  };

  return (
    <div className="max-w-6xl mx-auto px-6 pb-24 pt-6 animate-fade-in">
      <div className="grid lg:grid-cols-5 gap-16 items-start mb-20">
        {/* Left Column: Contact Info */}
        <div className="lg:col-span-2 space-y-8">
          <div>
            <h1 className="text-4xl md:text-5xl font-serif mb-6 text-stone-900 leading-tight">Let's start the conversation.</h1>
            <p className="text-lg text-stone-600 mb-6 leading-relaxed">
              Whether you're an ambitious woman ready to launch or a training institute looking to empower your students, we're here to help.
            </p>
            
            <div className="space-y-6 pt-4">
                <div className="flex gap-4">
                    <div className="bg-orange-100 p-2 h-fit rounded text-brand-dark"><User size={20}/></div>
                    <div>
                        <h4 className="font-bold text-stone-900 mb-1">For Ambitious Women</h4>
                        <p className="text-sm text-stone-600 leading-relaxed">
                            Ready to turn your skills into a profitable business? Fill out the application to see if our coaching programs are a good fit.
                        </p>
                    </div>
                </div>
                <div className="flex gap-4">
                    <div className="bg-orange-100 p-2 h-fit rounded text-brand-dark"><Building2 size={20}/></div>
                    <div>
                        <h4 className="font-bold text-stone-900 mb-1">For Institutions</h4>
                        <p className="text-sm text-stone-600 leading-relaxed">
                            Interested in partnering to offer entrepreneurial workshops or programs for your students? Let's discuss a custom collaboration.
                        </p>
                    </div>
                </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg border border-orange-100 shadow-sm mt-8">
             <h3 className="font-serif text-lg text-stone-900 mb-4 font-bold">What happens next?</h3>
             <ol className="space-y-4 text-stone-600 list-decimal list-outside ml-4 text-sm">
                <li className="pl-2">I personally review your inquiry.</li>
                <li className="pl-2">
                  You'll receive an email within 48 hours with:
                  <ul className="list-disc list-inside ml-2 mt-2 space-y-1 text-stone-500">
                    <li>Clear next steps</li>
                    <li>Answers to your specific questions</li>
                    <li>An invitation to book a call if relevant</li>
                  </ul>
                </li>
             </ol>
          </div>
        </div>

        {/* Right Column: Application Form */}
        <div className="lg:col-span-3 bg-white border border-orange-200 rounded-2xl shadow-xl overflow-hidden relative transition-all">
           {isSubmitted ? (
               <div className="p-16 text-center space-y-6">
                   <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                       <CheckCircle size={40} />
                   </div>
                   <h3 className="text-3xl font-serif text-stone-900">Message Received!</h3>
                   <p className="text-stone-600 text-lg">Thank you for reaching out. We'll review your details and get back to you within 48 hours.</p>
                   <button onClick={() => setIsSubmitted(false)} className="text-orange-600 font-bold hover:underline mt-8 inline-block">Submit another response</button>
               </div>
           ) : (
            <div className="p-8 md:p-10">
                <div className="mb-8 pb-8 border-b border-orange-100">
                    <h2 className="text-2xl font-serif text-stone-900 mb-4">Get in Touch</h2>
                    
                    {/* User Type Toggle */}
                    <div className="flex p-1 bg-stone-100 rounded-lg">
                        <button 
                            type="button"
                            onClick={() => setUserType('individual')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-bold transition-all ${userType === 'individual' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                        >
                            <User size={16} /> Individual
                        </button>
                        <button 
                            type="button"
                            onClick={() => setUserType('institution')}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-bold transition-all ${userType === 'institution' ? 'bg-white text-brand-dark shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
                        >
                            <Building2 size={16} /> Institution / Partner
                        </button>
                    </div>
                </div>
                
                {error && (
                  <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
                    <AlertCircle size={20} />
                    {error}
                  </div>
                )}

                {/* React Form */}
                <form 
                  name="contact"
                  method="post"
                  onSubmit={handleSubmit} 
                  className="space-y-6 animate-fade-in"
                >
                    {/* Hidden Netlify Fields */}
                    <input type="hidden" name="form-name" value="contact" />
                    <input type="hidden" name="user-type" value={userType} />

                    {/* Common Fields */}
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="name" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Name</label>
                            <input 
                                type="text" 
                                id="name" 
                                name="name" 
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="Your full name"
                            />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="email" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Email</label>
                            <input 
                                type="email" 
                                id="email" 
                                name="email" 
                                required
                                value={formData.email}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="name@example.com"
                            />
                        </div>
                    </div>

                    {/* INDIVIDUAL FORM FIELDS - CHANGED: Using CSS display instead of conditional rendering */}
                    <div className={`space-y-6 animate-fade-in ${userType === 'individual' ? 'block' : 'hidden'}`}>
                        <div className="space-y-2">
                            <label htmlFor="currentStatus" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Current Professional Status</label>
                            <select 
                                id="currentStatus" 
                                name="currentStatus" 
                                required={userType === 'individual'}
                                value={formData.currentStatus}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                            >
                                <option value="">Select an option...</option>
                                <option value="corporate_full_time">Working Corporate (Full-time)</option>
                                <option value="corporate_part_time">Working Corporate (Part-time)</option>
                                <option value="recently_quit">Recently Quit / In Transition</option>
                                <option value="full_time_business">Working on Business Full-time</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="skillToMonetize" className="text-sm font-bold text-stone-900 uppercase tracking-wide">What do you want to sell?</label>
                            <p className="text-xs text-stone-500 mb-2">Describe the skill, service, or expertise you want to turn into a business.</p>
                            <textarea 
                                id="skillToMonetize" 
                                name="skillToMonetize" 
                                required={userType === 'individual'}
                                rows={3}
                                value={formData.skillToMonetize}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="I want to offer..."
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="idealClient" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Who do you want to help?</label>
                            <textarea 
                                id="idealClient" 
                                name="idealClient" 
                                required={userType === 'individual'}
                                rows={2}
                                value={formData.idealClient}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="e.g. Overwhelmed agency owners..."
                            />
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="biggestChallenge" className="text-sm font-bold text-stone-900 uppercase tracking-wide">The Main Struggle</label>
                            <p className="text-xs text-stone-500 mb-2">What is the #1 thing stopping you from launching or growing right now?</p>
                            <textarea 
                                id="biggestChallenge" 
                                name="biggestChallenge" 
                                required={userType === 'individual'}
                                rows={3}
                                value={formData.biggestChallenge}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="e.g. I have too many ideas..."
                            />
                        </div>
                    </div>

                    {/* INSTITUTION FORM FIELDS - CHANGED: Using CSS display instead of conditional rendering */}
                    <div className={`space-y-6 animate-fade-in ${userType === 'institution' ? 'block' : 'hidden'}`}>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="space-y-2">
                                <label htmlFor="organizationName" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Organization Name</label>
                                <input 
                                    type="text" 
                                    id="organizationName" 
                                    name="organizationName" 
                                    required={userType === 'institution'}
                                    value={formData.organizationName}
                                    onChange={handleChange}
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                    placeholder="Institute / Academy Name"
                                />
                            </div>
                            <div className="space-y-2">
                                <label htmlFor="website" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Website (Optional)</label>
                                <input 
                                    type="text" 
                                    id="website" 
                                    name="website" 
                                    value={formData.website}
                                    onChange={handleChange}
                                    className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                    placeholder="www.example.com"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="partnershipType" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Partnership Interest</label>
                            <select 
                                id="partnershipType" 
                                name="partnershipType" 
                                required={userType === 'institution'}
                                value={formData.partnershipType}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                            >
                                <option value="">Select an option...</option>
                                <option value="guest_workshop">Guest Workshop / Masterclass</option>
                                <option value="curriculum_design">Curriculum Design / Review</option>
                                <option value="student_coaching">Student Business Coaching</option>
                                <option value="other">Other / Not Sure Yet</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <label htmlFor="message" className="text-sm font-bold text-stone-900 uppercase tracking-wide">Tell us about your goals</label>
                            <p className="text-xs text-stone-500 mb-2">Who are your students and what outcomes are you looking for?</p>
                            <textarea 
                                id="message" 
                                name="message" 
                                required={userType === 'institution'}
                                rows={4}
                                value={formData.message}
                                onChange={handleChange}
                                className="w-full p-3 bg-stone-50 border border-stone-200 rounded focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                                placeholder="We run a coding bootcamp and want to help our graduates start freelancing..."
                            />
                        </div>
                    </div>

                    <div className="pt-4">
                        <button type="submit" className="w-full bg-stone-900 text-white p-4 rounded-md font-bold hover:bg-stone-800 transition-all flex items-center justify-center gap-2 group">
                            {userType === 'individual' ? 'Submit Application' : 'Inquire About Partnership'} <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform"/>
                        </button>
                        <p className="text-center text-xs text-stone-400 mt-4">We respect your privacy. Your information is kept 100% confidential.</p>
                    </div>
                </form>
            </div>
           )}
        </div>
      </div>

      {/* Centered Footer Links */}
      <div className="max-w-xl mx-auto text-center space-y-6 pt-12 border-t border-orange-200">
         <h3 className="font-serif text-xl text-stone-900">Other ways to connect</h3>
         <div className="flex items-center justify-center gap-6">
           <a href="mailto:productivefolks@gmail.com" className="p-4 bg-white border border-orange-100 rounded-full text-stone-600 hover:bg-stone-900 hover:text-white transition-all shadow-sm hover:shadow-md" title="Email Us">
             <Mail size={24} />
           </a>
           <a href="https://www.linkedin.com/company/productive-folks/" target="_blank" rel="noopener noreferrer" className="p-4 bg-white border border-orange-100 rounded-full text-stone-600 hover:bg-stone-900 hover:text-white transition-all shadow-sm hover:shadow-md" title="LinkedIn">
             <Linkedin size={24} />
           </a>
           <a href="https://instagram.com/productive_folks" target="_blank" rel="noopener noreferrer" className="p-4 bg-white border border-orange-100 rounded-full text-stone-600 hover:bg-stone-900 hover:text-white transition-all shadow-sm hover:shadow-md" title="Instagram">
             <Instagram size={24} />
           </a>
         </div>
      </div>
    </div>
  );
};

export default Contact;